import React, { useMemo, useState } from 'react';
import { amActions } from '../amStore';
import { COLORS, OTD_TARGET, SEVERITY_DEFAULTS } from '../amConfig';
import { EXCUSED_CATEGORIES, monthLabel } from '../amRules';
import {
  shipmentKpis, groupBy, periodKey, periodLabel, otdDrops, severityRanking, groupStats, adherence,
} from '../amMetrics';
import {
  Panel, Seg, KpiCard, makeDelta, ShareBar, Chart, clickTo, SortTable, DrillTable, perfColumns, RankedBars,
  fmtInt, fmtNum, fmtPct, ragColor,
} from '../amUi';
import { monthComparison, DIM_FILTER } from './common';

const ORIGIN_LEVELS = [
  { field: 'srcCity', label: 'Origin', filterKey: 'origin' },
  { field: 'dstCity', label: 'Destination', filterKey: 'dstCity' },
  { field: 'lane', label: 'Lane', filterKey: 'lane' },
];
const DEST_LEVELS = [
  { field: 'dstZone', label: 'Zone', filterKey: 'dstZone' },
  { field: 'dstState', label: 'State', filterKey: 'dstState' },
  { field: 'dstCity', label: 'City', filterKey: 'dstCity' },
];
const ATTENTION_TABS = [
  { value: 'transporter', label: 'Transporters' },
  { value: 'lane', label: 'Lanes' },
  { value: 'srcCity', label: 'Origins' },
  { value: 'dstCity', label: 'Destinations' },
];
const EXC_DIMS = [
  { value: 'transporter', label: 'Transporter' }, { value: 'srcCity', label: 'Origin' },
  { value: 'dstCity', label: 'Destination city' }, { value: 'dstZone', label: 'Zone' },
  { value: 'dstState', label: 'State' }, { value: 'lane', label: 'Lane' }, { value: 'month', label: 'Month' },
];
const EXC_COLORS = { 'Customer Issue': COLORS.steel[1], 'Local/Public Holidays': COLORS.steel[3], 'Natural Calamity': COLORS.steel[4] };
const ADH_COLORS = { Early: COLORS.steel[2], 'On date': COLORS.green, Late: COLORS.red };

function KpiStrip({ s, rows, k, openExplorer }) {
  const cmp = useMemo(() => monthComparison(s, rows), [s, rows]);
  const c = cmp && shipmentKpis(cmp.curRows);
  const p = cmp && shipmentKpis(cmp.prevRows);
  const d = (f, o) => (cmp ? makeDelta(c[f], p[f], { label: cmp.label, ...o }) : null);
  const on = (key, v) => s.filters[key].length === 1 && s.filters[key][0] === v;
  const excusedOn = s.filters.exception.length === EXCUSED_CATEGORIES.length
    && EXCUSED_CATEGORIES.every((x) => s.filters.exception.includes(x));
  return (
    <>
      <div className="am-kpis seven">
        <KpiCard label="Total shipments" value={fmtInt(k.total)} sub="LR records · open shipment list" delta={d('total')}
          onClick={openExplorer} hint="Open these shipments in the explorer" />
        <KpiCard label="Total delivered" value={fmtInt(k.delivered)} sub={`${fmtInt(k.notDelivered)} not yet delivered`} delta={d('delivered')}
          onClick={() => amActions.toggleSet('onTime', ['On Time', 'Delay'])}
          active={s.filters.onTime.length === 2 && !s.filters.onTime.includes('Not delivered')} hint="Show delivered LRs only" />
        <KpiCard label="OTD" unit="%" value={fmtPct(k.otd)} accent={ragColor(k.otd)} sub={`${fmtInt(k.onTime)} on time · target ${OTD_TARGET}%`}
          delta={d('otd', { higherIsBetter: true, isPct: true })}
          onClick={() => amActions.toggle('onTime', 'On Time')} active={on('onTime', 'On Time')} hint="Show on-time LRs only" />
        <KpiCard label="Delayed" value={fmtInt(k.delayed)} sub={`${fmtPct(k.delayPct)} of delivered`}
          delta={d('delayed', { higherIsBetter: false })}
          onClick={() => amActions.toggle('onTime', 'Delay')} active={on('onTime', 'Delay')} hint="Show delayed LRs only" />
        <KpiCard label="Avg delivery" unit="days" value={fmtNum(k.avgDeliveryDays)} sub="Dispatch to delivery"
          delta={d('avgDeliveryDays', { higherIsBetter: false, unit: 'days' })}
          onClick={() => openExplorer({ sort: 'deliveryDays' })} hint="Open shipments, slowest deliveries first" />
        <KpiCard label="Avg breach" unit="days" value={fmtNum(k.avgBreachDays)} sub="Delayed LRs only"
          delta={d('avgBreachDays', { higherIsBetter: false, unit: 'days' })}
          onClick={() => amActions.toggle('onTime', 'Delay')} active={on('onTime', 'Delay')} hint="Show delayed LRs (breach applies to them)" />
        <KpiCard label="Exception shipments" value={fmtInt(k.excused)} sub="Excused delays, counted as on time" delta={d('excused')}
          onClick={() => amActions.toggleSet('exception', EXCUSED_CATEGORIES)} active={excusedOn} hint="Show excused delays only" />
      </div>
      {cmp && <p className="am-note">Changes compare {monthLabel(cmp.cur)} with {monthLabel(cmp.prev)} under the same non-date filters. {monthLabel(cmp.cur)} may still have shipments in transit. Click any card to filter the dashboard.</p>}
    </>
  );
}

function OtdHero({ k }) {
  const color = ragColor(k.otd);
  return (
    <Panel title="Overall OTD" subtitle="Delivered LRs, New Performance rule" className="am-hero">
      <div className="am-hero-value" style={{ color }}>{fmtPct(k.otd)}</div>
      <div className="am-hero-bar">
        <div className="am-hero-fill" style={{ width: `${Math.min(100, k.otd || 0)}%`, background: color }} />
        <div className="am-hero-target" style={{ left: `${OTD_TARGET}%` }}><span>Target {OTD_TARGET}%</span></div>
      </div>
      <div className="am-hero-stats">
        <div><b>{fmtInt(k.delivered)}</b><span>Delivered</span></div>
        <div className="clickable" onClick={() => amActions.toggle('onTime', 'On Time')}>
          <b style={{ color: COLORS.green }}>{fmtInt(k.onTime)}</b><span>On time<br /><small>incl. {fmtInt(k.excused)} excused</small></span>
        </div>
        <div className="clickable" onClick={() => amActions.toggle('onTime', 'Delay')}>
          <b style={{ color: COLORS.red }}>{fmtInt(k.delayed)}</b><span>Delay</span>
        </div>
      </div>
      <div className="am-hero-gap">
        {k.otd === null ? 'No delivered LRs in view.'
          : k.otd >= OTD_TARGET ? `${fmtNum(k.otd - OTD_TARGET)} pts above target`
            : `${fmtNum(OTD_TARGET - k.otd)} pts below target · ${fmtInt(Math.ceil((OTD_TARGET / 100) * k.delivered - k.onTime))} more on-time LRs needed`}
      </div>
    </Panel>
  );
}

function OtdTrend({ rows }) {
  const [grain, setGrain] = useState('month');
  const series = useMemo(() => [...groupBy(rows, (r) => periodKey(r, grain)).entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([key, items]) => ({ key, label: periodLabel(key, grain), ...shipmentKpis(items) })), [rows, grain]);
  const drops = otdDrops(series, 2);
  // One % axis only: both bars are shares of delivered LRs, so the On time bar
  // IS the OTD % and reads directly against the 85% target line. (Counts on a
  // second axis made the bars look higher than OTD.)
  const notDelivered = series.reduce((a, p) => a + p.notDelivered, 0);
  const options = useMemo(() => {
    const share = (n, p) => (p.delivered ? +((n / p.delivered) * 100).toFixed(1) : null);
    return {
      chart: { type: 'column' },
      xAxis: { categories: series.map((p) => p.label) },
      yAxis: {
        title: { text: '% of delivered LRs' }, min: 0, max: 100, endOnTick: false, tickInterval: 20, labels: { format: '{value}%' },
        plotLines: [{ value: OTD_TARGET, color: COLORS.navy, dashStyle: 'Dash', width: 1.5, zIndex: 5, label: { text: `Target ${OTD_TARGET}%`, align: 'right', y: -5, style: { fontSize: '10.5px', fontWeight: '700', color: COLORS.navy } } }],
      },
      plotOptions: {
        column: { grouping: true, groupPadding: 0.18, pointPadding: 0.04, maxPointWidth: 64 },
        series: {
          dataLabels: { enabled: true, format: '{y}%', crop: false, overflow: 'allow', style: { fontSize: '11px', fontWeight: '700', textOutline: 'none' } },
          ...(grain === 'month' ? clickTo((cat, pt) => amActions.toggle('month', series[pt.index].key)) : {}),
        },
      },
      tooltip: {
        shared: true,
        formatter() {
          const p = series[this.points[0].point.index];
          return `<b>${p.label}</b><br/>`
            + `<span style="color:${COLORS.green}">●</span> On time (OTD): <b>${fmtPct(p.otd)}</b> · ${fmtInt(p.onTime)} LRs (incl. ${fmtInt(p.excused)} excused)<br/>`
            + `<span style="color:${COLORS.red}">●</span> Delay: <b>${fmtPct(p.delayPct)}</b> · ${fmtInt(p.delayed)} LRs<br/>`
            + `Delivered: ${fmtInt(p.delivered)} · not yet delivered: ${fmtInt(p.notDelivered)}<br/>`
            + `${p.otd !== null && p.otd >= OTD_TARGET ? 'Meets' : 'Below'} the ${OTD_TARGET}% target`;
        },
      },
      series: [
        { name: 'On time % (OTD)', color: COLORS.green, data: series.map((p) => share(p.onTime, p)), dataLabels: { color: COLORS.green } },
        { name: 'Delay %', color: COLORS.red, data: series.map((p) => share(p.delayed, p)), dataLabels: { color: COLORS.red } },
      ],
    };
  }, [series, grain]);
  return (
    <Panel title="OTD trend" subtitle={`On time vs delay as % of delivered LRs, by dispatch period · click a month to filter${notDelivered ? ` · ${fmtInt(notDelivered)} not-yet-delivered LRs excluded` : ''}`}
      actions={<Seg size="sm" value={grain} onChange={setGrain} options={[{ value: 'year', label: 'Year' }, { value: 'quarter', label: 'Quarter' }, { value: 'month', label: 'Month' }]} />}>
      <Chart options={options} height={290} />
      <div className="am-drops">
        {drops.length === 0
          ? <span className="ok">No period-on-period OTD drop of 2 pts or more.</span>
          : drops.map((x) => <span key={x.to} className="bad">▼ {x.from} → {x.to}: −{fmtNum(x.drop)} pts (to {fmtPct(x.otd)})</span>)}
      </div>
    </Panel>
  );
}

/** Plain-language line for one severity driver (prototype wording). */
function driverText(d, it) {
  switch (d) {
    case 'volume': return `High volume: ${fmtInt(it.total)} shipments`;
    case 'gap': return `OTD ${fmtPct(it.otd)}, ${fmtNum(it.gap)} pts below target`;
    case 'drop': return `OTD fell ${fmtNum(it.drop)} pts (${fmtPct(it.otdBefore)} → ${fmtPct(it.otdNow)})`;
    case 'breach': return `Avg breach ${fmtNum(it.avgBreachDays)} days on delayed`;
    case 'exceptions': return `${fmtInt(it.excused)} exceptions (excused delays)`;
    default: return '';
  }
}

function Attention({ s, rows }) {
  const [tab, setTab] = useState('transporter');
  const [tuning, setTuning] = useState(false);
  const opts = s.severity;
  const items = useMemo(() => severityRanking(rows, tab, opts, 5), [rows, tab, opts]);
  const setW = (k, v) => amActions.setSeverity({ ...opts, weights: { ...opts.weights, [k]: v } });
  const fk = DIM_FILTER[tab];
  return (
    <Panel title="Top 5 attention required" subtitle={`Below ${OTD_TARGET}% OTD or down ≥1 pt month on month, min ${opts.minVolume} shipments · click to filter`}
      actions={(
        <>
          <Seg size="sm" value={tab} onChange={setTab} options={ATTENTION_TABS} />
          <button type="button" className={`am-btn ghost sm ${tuning ? 'on' : ''}`} onClick={() => setTuning((v) => !v)}>Scoring</button>
        </>
      )}>
      {tuning && (
        <div className="am-tuning">
          {[['volume', 'Volume'], ['gap', `OTD gap to ${OTD_TARGET}%`], ['drop', 'MoM OTD drop'], ['breach', 'Avg breach days'], ['exceptions', 'Exceptions']].map(([k, l]) => (
            <label key={k}>{l}<input type="range" min="0" max="50" value={opts.weights[k]} onChange={(e) => setW(k, Number(e.target.value))} /><b>{opts.weights[k]}</b></label>
          ))}
          <label>Min shipments<input type="range" min="0" max="500" step="10" value={opts.minVolume} onChange={(e) => amActions.setSeverity({ ...opts, minVolume: Number(e.target.value) })} /><b>{opts.minVolume}</b></label>
          <button type="button" className="am-link" onClick={() => amActions.setSeverity(SEVERITY_DEFAULTS)}>Restore defaults</button>
        </div>
      )}
      {items.length === 0
        ? <div className="am-empty">Nothing needs attention under the current filters.</div>
        : (
          <div className="am-attn">
            {items.map((it, i) => (
              <button type="button" key={it.name} className={`am-attn-card ${s.filters[fk].includes(it.name) ? 'sel' : ''}`}
                onClick={() => amActions.toggle(fk, it.name)}>
                <div className="am-attn-head">
                  <span className="am-rank">{i + 1}</span>
                  <span className="am-score" title="Severity score (0–100)">{it.score.toFixed(0)}</span>
                </div>
                <div className="am-attn-name" title={it.name}>{it.name}</div>
                <div className="am-attn-metrics">
                  <span><b>{fmtInt(it.total)}</b> shipments</span>
                  <span>OTD <b style={{ color: ragColor(it.otd) }}>{fmtPct(it.otd)}</b></span>
                </div>
                {/* Always rendered so every card's bullet list starts at the same height. */}
                <div className="am-attn-mom">{it.drop >= 1 ? <span className="bad">▼ {fmtNum(it.drop)} pts MoM</span> : ' '}</div>
                <ul>
                  {it.drivers.map((d) => <li key={d}>{driverText(d, it)}</li>)}
                </ul>
              </button>
            ))}
          </div>
        )}
    </Panel>
  );
}

function Scorecard({ s, rows }) {
  const stats = useMemo(() => groupStats(rows, 'transporter'), [rows]);
  return (
    <Panel title="3PL scorecard" subtitle="Click a transporter to filter · avg breach of 5+ days in bold red">
      <SortTable columns={perfColumns('Transporter')} rows={stats} defaultSort={{ key: 'total', dir: 'desc' }} maxRows={15}
        onRowClick={(r) => amActions.toggle('transporter', r.name)} isSelected={(r) => s.filters.transporter.includes(r.name)} />
    </Panel>
  );
}

function Exceptions({ s, rows, k }) {
  const [dim, setDim] = useState('transporter');
  const [showAll, setShowAll] = useState(false);
  const exc = useMemo(() => rows.filter((r) => r.excused), [rows]);
  const delayRemarked = useMemo(() => rows.filter((r) => r.perf === 'Delay').length, [rows]);
  const ranked = useMemo(() => [...groupBy(exc, dim).entries()].map(([name, items]) => {
    const by = {}; items.forEach((r) => { by[r.remarkCat] = (by[r.remarkCat] || 0) + 1; });
    return {
      name, label: dim === 'month' ? monthLabel(name) : name, total: items.length,
      segments: EXCUSED_CATEGORIES.map((c) => ({ key: c, count: by[c] || 0 })),
    };
  }).sort((a, b) => (dim === 'month' ? a.name.localeCompare(b.name) : b.total - a.total)), [exc, dim]);
  const shown = showAll ? ranked : ranked.slice(0, 15);
  const fk = DIM_FILTER[dim];
  return (
    <Panel title="Exceptions (excused delays)"
      subtitle={`Delays excused by remark and counted inside on-time LRs · ${fmtInt(k.excused)} of ${fmtInt(delayRemarked)} delays reported by carriers`}>
      <div className="am-reasons">
        {EXCUSED_CATEGORIES.map((c) => {
          const n = k.excusedBy[c] || 0;
          const sel = s.filters.exception.length === 1 && s.filters.exception[0] === c;
          return (
            <button type="button" key={c} className={`am-reason ${sel ? 'sel' : ''}`} style={{ '--c': EXC_COLORS[c] }}
              onClick={() => amActions.toggle('exception', c)} title={`Filter to ${c} exceptions`}>
              <span className="am-reason-name">{c}</span>
              <b>{fmtInt(n)}</b>
              <span className="am-reason-meta">{fmtPct(k.excused ? (n / k.excused) * 100 : null)} of exceptions · {fmtPct(k.delivered ? (n / k.delivered) * 100 : null)} of delivered</span>
              <span className="am-reason-bar"><i style={{ width: `${k.excused ? (n / k.excused) * 100 : 0}%` }} /></span>
            </button>
          );
        })}
      </div>
      <div className="am-rank-head">
        <h4 className="am-sub">Exceptions ranked by {EXC_DIMS.find((x) => x.value === dim).label.toLowerCase()}</h4>
        <Seg size="sm" value={dim} onChange={(v) => { setDim(v); setShowAll(false); }} options={EXC_DIMS} />
      </div>
      <RankedBars items={shown} valueLabel="excused delays" selected={s.filters[fk] || []}
        series={EXCUSED_CATEGORIES.map((c) => ({ key: c, label: c, color: EXC_COLORS[c] }))}
        onSelect={(v) => amActions.toggle(fk, v)} />
      {ranked.length > 15 && (
        <button type="button" className="am-link am-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show top 15' : `Show all ${fmtInt(ranked.length)}`}
        </button>
      )}
    </Panel>
  );
}

function Adherence({ s, rows }) {
  const [view, setView] = useState('share');
  const [ref, setRef] = useState('etaRes');
  const refs = [{ value: 'etaRes', label: 'vs ETA', filterKey: 'etaAdh' }, { value: 'cpdRes', label: 'vs CPD', filterKey: 'cpdAdh' }, { value: 'eddRes', label: 'vs EDD', filterKey: 'eddAdh' }];
  const all = refs.map((r) => ({ ...r, a: adherence(rows, r.value) }));
  const months = useMemo(() => [...groupBy(rows, 'month').entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([m, items]) => ({ m, a: adherence(items, ref) })), [rows, ref]);
  const tableRows = (field) => groupStats(rows, field).map((g) => ({
    name: g.name, total: g.total, eta: adherence(g.items, 'etaRes'), cpd: adherence(g.items, 'cpdRes'), edd: adherence(g.items, 'eddRes'),
  }));
  const tptRows = useMemo(() => tableRows('transporter'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const laneRows = useMemo(() => tableRows('lane'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const adhCols = (name) => [
    { key: 'name', label: name, render: (r) => <span className="am-name">{r.name}</span> },
    { key: 'total', label: 'Shipments', align: 'right', bar: true, render: (r) => fmtInt(r.total) },
    ...['eta', 'cpd', 'edd'].map((f) => ({
      key: f, label: `${f.toUpperCase()} adherence %`, align: 'right', value: (r) => r[f].pct,
      render: (r) => (r[f].n ? <b style={{ color: ragColor(r[f].pct) }} title={`${fmtInt(r[f].early + r[f].onDate)} of ${fmtInt(r[f].n)}`}>{fmtPct(r[f].pct)}</b> : <span className="muted">Data unavailable</span>),
    })),
  ];
  const monthly = useMemo(() => ({
    chart: { type: 'column' },
    xAxis: { categories: months.map((x) => monthLabel(x.m)) },
    yAxis: { max: 100, endOnTick: false, title: { text: '% of delivered LRs' }, labels: { format: '{value}%' } },
    plotOptions: { column: { stacking: 'percent', maxPointWidth: 56, pointPadding: 0.1, groupPadding: 0.2 }, series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index].m)) },
    tooltip: { shared: true, pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:,.0f}</b> ({point.percentage:.1f}%)<br/>', footerFormat: '<span style="font-size:10px;color:#6b778c">Click to filter to this month</span>' },
    series: [['Early', 'early'], ['On date', 'onDate'], ['Late', 'late']].map(([n, f]) => ({ name: n, color: ADH_COLORS[n], data: months.map((x) => x.a[f]) })),
  }), [months]);
  return (
    <Panel title="Delivery adherence" subtitle="Pure date checks against each reference date · excused remarks not applied, so ETA adherence differs from OTD"
      actions={(
        <>
          {view === 'monthly' && <Seg size="sm" value={ref} onChange={setRef} options={refs} />}
          <Seg size="sm" value={view} onChange={setView} options={[{ value: 'share', label: 'Overall' }, { value: 'monthly', label: 'Monthly' }]} />
        </>
      )}>
      {view === 'share' ? (
        <div className="am-adh-grid">
          {all.map((r) => (
            <div key={r.value} className="am-adh">
              <div className="am-adh-head"><span>{r.label}</span><b style={{ color: ragColor(r.a.pct) }}>{fmtPct(r.a.pct)}</b></div>
              <ShareBar items={[['Early', 'early'], ['On date', 'onDate'], ['Late', 'late']].map(([n, f]) => ({ name: n, count: r.a[f], color: ADH_COLORS[n] }))}
                selected={s.filters[r.filterKey]} onSelect={(v) => amActions.toggle(r.filterKey, v)} />
              <small>{fmtInt(r.a.n)} delivered LRs with {r.value === 'cpdRes' ? 'a' : 'an'} {r.label.slice(3)}</small>
            </div>
          ))}
        </div>
      ) : <Chart options={monthly} height={270} />}
      <div className="am-grid two tight">
        <div><h4 className="am-sub">Transporter-wise</h4><SortTable columns={adhCols('Transporter')} rows={tptRows} defaultSort={{ key: 'total', dir: 'desc' }} maxRows={8} onRowClick={(r) => amActions.toggle('transporter', r.name)} /></div>
        <div><h4 className="am-sub">Lane-wise</h4><SortTable columns={adhCols('Lane')} rows={laneRows} defaultSort={{ key: 'total', dir: 'desc' }} maxRows={8} onRowClick={(r) => amActions.toggle('lane', r.name)} /></div>
      </div>
    </Panel>
  );
}

export default function ExecutivePage({ ctx }) {
  const { s, rows, drillPath, drillToExplorer } = ctx;
  const k = useMemo(() => shipmentKpis(rows), [rows]);
  const [perfView, setPerfView] = useState('origin');
  return (
    <>
      <KpiStrip s={s} rows={rows} k={k} openExplorer={(opts) => drillToExplorer({}, opts)} />
      <div className="am-grid hero">
        <OtdHero k={k} />
        <OtdTrend rows={rows} />
      </div>
      <Attention s={s} rows={rows} />
      <Scorecard s={s} rows={rows} />
      <Panel title={perfView === 'origin' ? 'Origin performance' : 'Destination performance'}
        subtitle={perfView === 'origin' ? 'Origin → destination → lane → shipments' : 'Zone → state → city → shipments'}
        actions={<Seg size="sm" value={perfView} onChange={setPerfView} options={[{ value: 'origin', label: 'Origin' }, { value: 'dest', label: 'Destination' }]} />}>
        <DrillTable rows={rows} levels={perfView === 'origin' ? ORIGIN_LEVELS : DEST_LEVELS} onLeaf={(path) => drillPath(path)} />
      </Panel>
      <Exceptions s={s} rows={rows} k={k} />
      <Adherence s={s} rows={rows} />
    </>
  );
}
